import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAudit } from "@/modules/audit/presentation/api/client";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { payoutApi } from "./payout-client";
import { walletKeys } from "./queries";

export const payoutKeys = {
  all: ["payout"] as const,
  stats: () => ["payout", "stats"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["payout", "list", q] as const,
  detail: (id: string) => ["payout", "detail", id] as const,
  audit: (id: string) => ["payout", "audit", id] as const,
};

export function usePayoutStatsQuery() {
  return useQuery({
    queryKey: payoutKeys.stats(),
    queryFn: () => payoutApi.getStats(),
  });
}

export function usePayoutListQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: payoutKeys.list(q),
    queryFn: () => payoutApi.list(q),
  });
}

export function usePayoutDetailQuery(id: string) {
  return useQuery({
    queryKey: payoutKeys.detail(id),
    queryFn: () => payoutApi.getDetail(id),
    enabled: !!id,
  });
}

export function useCreatePayoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof payoutApi.create>[0]) =>
      payoutApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
      qc.invalidateQueries({ queryKey: walletKeys.overview() });
      toast.success("ສ້າງຄຳຂໍຈ່າຍເງິນສຳເລັດ");
    },
    onError: () => {
      toast.error("ສ້າງຄຳຂໍຈ່າຍເງິນລົ້ມເຫຼວ");
    },
  });
}

export function usePayoutAuditQuery(entityId: string) {
  return useQuery({
    queryKey: payoutKeys.audit(entityId),
    queryFn: () =>
      fetchAudit({
        limit: 50,
        offset: 0,
        sort: [{ field: "occurredAt", dir: "desc" }],
        filters: [
          { field: "entityType", op: "eq", value: "payout_request" },
          { field: "entityId", op: "eq", value: entityId },
        ],
      }),
    enabled: !!entityId,
  });
}

export function useApprovePayoutMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => payoutApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
      qc.invalidateQueries({ queryKey: payoutKeys.detail(id) });
      qc.invalidateQueries({ queryKey: payoutKeys.audit(id) });
      toast.success("ອະນຸມັດການຈ່າຍເງິນສຳເລັດ");
    },
    onError: () => {
      toast.error("ອະນຸມັດລົ້ມເຫຼວ");
    },
  });
}

export function useProcessPayoutMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => payoutApi.process(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
      qc.invalidateQueries({ queryKey: payoutKeys.detail(id) });
      qc.invalidateQueries({ queryKey: payoutKeys.audit(id) });
      qc.invalidateQueries({ queryKey: walletKeys.overview() });
      toast.success("ດຳເນີນການຈ່າຍເງິນສຳເລັດ");
    },
    onError: () => {
      toast.error("ດຳເນີນການຈ່າຍເງິນລົ້ມເຫຼວ");
    },
  });
}
